import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Calendar, DollarSign, Users, RefreshCw, TrendingDown } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatCents } from '../utils/currencyFormat';
import { MONTHLY_USAGE_COLUMNS } from '../constants/monthlyUsageColumns';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f43f5e', '#84cc16', '#6366f1'];

const PieChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="bg-white border border-secondary-200 rounded-md shadow-lg px-3 py-2 text-sm">
      <p className="font-medium text-secondary-900">{data.name}</p>
      <p className="text-secondary-600">Spend: ${data.value.toFixed(2)}</p>
    </div>
  );
};

const TrendBadge = ({ status }) => {
  if (!status) return <span className="text-secondary-400">—</span>;

  const styles = {
    above: 'badge-warning',
    below: 'badge-success',
    on_track: 'badge-secondary'
  };
  const labels = {
    above: 'Above trend',
    below: 'Below trend',
    on_track: 'On track'
  };

  return <span className={`badge ${styles[status] || 'badge-secondary'}`}>{labels[status]}</span>;
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MonthlyUsage = () => {
  const [loading, setLoading] = useState(true);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [summary, setSummary] = useState([]);
  const [totalSpendCents, setTotalSpendCents] = useState(0);
  const [annualData, setAnnualData] = useState(null);
  const [costAllocation, setCostAllocation] = useState(null);
  const [fiscalSettings, setFiscalSettings] = useState({ fiscalYearStartMonth: 1 });
  const [refreshing, setRefreshing] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get(endpoints.billingGroups.settings);
      if (res.data) {
        setFiscalSettings({
          fiscalYearStartMonth: res.data.fiscalYearStartMonth || 1,
          fiscalYearStartMonthName: res.data.fiscalYearStartMonthName || 'January'
        });
      }
    } catch (e) {
      // non-fatal
    }
  }, []);

  const loadAvailableMonths = useCallback(async () => {
    try {
      const res = await api.get(endpoints.billingGroups.availableMonths);
      const months = (res.data?.months || []).map((item) => ({
        year: item.Year,
        month: item.Month,
        label: format(new Date(item.Year, item.Month - 1), 'MMMM yyyy'),
        totalSpendCents: item.TotalSpendCents,
        groupCount: item.GroupCount,
        status: item.Status
      }));
      setAvailableMonths(months);
      if (months.length > 0 && !selectedMonth) {
        setSelectedMonth({ year: months[0].year, month: months[0].month });
      }
    } catch (error) {
      console.error('Failed to load available months:', error);
      toast.error('Failed to load available months');
    }
  }, [selectedMonth]);

  const loadReportData = useCallback(async () => {
    if (!selectedMonth) return;
    try {
      setLoading(true);
      const { year, month } = selectedMonth;

      const [summaryRes, annualRes, allocationRes] = await Promise.all([
        api.get(`${endpoints.billingGroups.summary}?year=${year}&month=${month}`),
        api.get(`${endpoints.billingGroups.annualSummary}?year=${year}&month=${month}`),
        api.get(`${endpoints.billingGroups.costAllocation}?year=${year}&month=${month}`)
      ]);

      setSummary(summaryRes.data?.summary || []);
      setTotalSpendCents(summaryRes.data?.totalSpendCents || 0);
      setAnnualData(annualRes.data || null);
      setCostAllocation(allocationRes.data || null);
    } catch (error) {
      console.error('Failed to load monthly usage data:', error);
      toast.error('Failed to load monthly usage data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    loadSettings();
    loadAvailableMonths();
  }, [loadSettings, loadAvailableMonths]);

  useEffect(() => {
    if (selectedMonth) {
      loadReportData();
    }
  }, [selectedMonth, loadReportData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAvailableMonths();
    loadReportData();
  };

  const getChartData = () => {
    return summary
      .filter((g) => parseFloat(g.SpendCents || 0) > 0)
      .map((g) => ({
        name: g.GroupName,
        value: parseFloat(g.SpendCents || 0) / 100,
        spendCents: g.SpendCents,
        memberCount: g.MemberCount,
        percentage: totalSpendCents > 0
          ? ((g.SpendCents / totalSpendCents) * 100).toFixed(1)
          : '0.0'
      }));
  };

  const getFiscalYearLabel = () => annualData?.fiscalYearLabel || '';

  const fiscalYearStartMonth = annualData?.fiscalYearStartMonth || fiscalSettings.fiscalYearStartMonth;
  const fiscalYearStartMonthName = annualData?.fiscalYearStartMonthName
    || fiscalSettings.fiscalYearStartMonthName
    || MONTH_NAMES[fiscalYearStartMonth - 1];

  const annualTotalCents = (annualData?.groups || []).reduce(
    (s, g) => s + parseFloat(g.TotalSpendCents || 0),
    0
  );

  const allocationTotals = costAllocation?.totals || {};
  const configuredAllocationGroups = (costAllocation?.groups || []).filter((g) => g.isConfigured);
  const hasCostAllocation = (costAllocation?.groups || []).length > 0;
  const allocationByGroupId = new Map(
    (costAllocation?.groups || []).map((g) => [g.cursorGroupId, g])
  );
  const monthsElapsed = costAllocation?.monthsElapsedInFiscalYear;
  const isTrendProrated = costAllocation?.isTrendProrated;
  const trendProrationNote = costAllocation?.trendProrationNote;
  const fiscalYearLabel = costAllocation?.fiscalYearLabel;

  const trendSubtitle = (() => {
    if (monthsElapsed == null) return null;
    if (isTrendProrated && trendProrationNote) {
      return `Prepaid usage trend based on ${trendProrationNote} elapsed in fiscal year${fiscalYearLabel ? ` (${fiscalYearLabel})` : ''}`;
    }
    return `Prepaid usage trend based on ${monthsElapsed} month${monthsElapsed !== 1 ? 's' : ''} elapsed in fiscal year${fiscalYearLabel ? ` (${fiscalYearLabel})` : ''}`;
  })();

  const chartData = getChartData();
  const barChartWidth = Math.max(chartData.length * 88, 640);
  const isInProgress = summary.some((r) => r.Status === 'in_progress');

  if (loading && !summary.length && !availableMonths.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Monthly Usage</h1>
          <p className="text-secondary-600 mt-1">
            Cursor spend by billing group · Fiscal year starts in {fiscalYearStartMonthName}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {availableMonths.length > 0 && (
            <select
              className="input"
              value={selectedMonth ? `${selectedMonth.year}-${selectedMonth.month}` : ''}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map(Number);
                setSelectedMonth({ year: y, month: m });
              }}
            >
              {availableMonths.map((m) => (
                <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                  {m.label}{m.status === 'in_progress' ? ' (in progress)' : ''}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn btn-outline flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {availableMonths.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-12">
            <Calendar className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-secondary-900 mb-2">No billing group data yet</h3>
            <p className="text-secondary-600">
              Data will appear after the nightly billing group import runs, or after an admin runs a backfill.
            </p>
          </div>
        </div>
      ) : (
        <>
          {isInProgress && (
            <div className="card border-warning-200 bg-warning-50">
              <div className="card-body text-warning-800 text-sm">
                This month is still in progress — figures shown are month-to-date.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">{formatCents(totalSpendCents)}</div>
                  <div className="stat-label">Monthly Total</div>
                </div>
                <DollarSign className="w-8 h-8 text-primary-600" />
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">{summary.length}</div>
                  <div className="stat-label">Billing Groups</div>
                </div>
                <Users className="w-8 h-8 text-success-600" />
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">{formatCents(annualTotalCents)}</div>
                  <div className="stat-label">Fiscal YTD</div>
                </div>
                <Calendar className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>

          {chartData.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="card lg:col-span-1">
                <div className="card-header">
                  <h3 className="text-lg font-semibold">Spend by Billing Group</h3>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card lg:col-span-3">
                <div className="card-header">
                  <h3 className="text-lg font-semibold">Spend Comparison</h3>
                </div>
                <div className="card-body overflow-x-auto">
                  <div style={{ width: barChartWidth, height: 380, minWidth: '100%' }}>
                    <BarChart
                      data={chartData}
                      width={barChartWidth}
                      height={380}
                      margin={{ top: 8, right: 16, left: 8, bottom: 96 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={90}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tickFormatter={(v) => `$${v}`} width={72} />
                      <Tooltip formatter={(value) => [`$${Number(value).toFixed(2)}`, 'Spend']} />
                      <Bar dataKey="value" fill="#3b82f6" maxBarSize={56} />
                    </BarChart>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Monthly Spend by Billing Group</h3>
              {trendSubtitle && (
                <p className="text-sm text-secondary-500 mt-1">
                  {trendSubtitle}
                </p>
              )}
            </div>
            <div className="card-body overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th title={MONTHLY_USAGE_COLUMNS[0].description}>Billing Group</th>
                    <th className="text-right" title={MONTHLY_USAGE_COLUMNS[1].description}>Members</th>
                    <th className="text-right" title={MONTHLY_USAGE_COLUMNS[2].description}>Monthly Spend</th>
                    <th className="text-right" title={MONTHLY_USAGE_COLUMNS[3].description}>Yearly Prepaid</th>
                    <th className="text-right" title={MONTHLY_USAGE_COLUMNS[4].description}>YTD Consumed</th>
                    <th title={MONTHLY_USAGE_COLUMNS[5].description}>Usage Trend</th>
                    <th title={MONTHLY_USAGE_COLUMNS[6].description}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((row) => {
                    const allocation = allocationByGroupId.get(row.CursorGroupId);
                    return (
                      <tr key={row.CursorGroupId}>
                        <td className="font-medium">{row.GroupName}</td>
                        <td className="text-right">{row.MemberCount}</td>
                        <td className="text-right">{formatCents(row.SpendCents)}</td>
                        <td className="text-right">
                          {allocation?.isConfigured
                            ? formatCents(allocation.committedPrepaidCents)
                            : <span className="text-secondary-400">Not set</span>}
                        </td>
                        <td className="text-right">
                          {allocation?.isConfigured ? (
                            <span>
                              {formatCents(allocation.fytdSpendCents)}
                              {allocation.percentDrawn != null && (
                                <span className="text-secondary-500 text-xs block">
                                  {allocation.percentDrawn.toFixed(1)}% of prepaid
                                </span>
                              )}
                            </span>
                          ) : '—'}
                        </td>
                        <td>
                          <TrendBadge status={allocation?.trendStatus} />
                        </td>
                        <td>
                          <span className={`badge ${row.Status === 'final' ? 'badge-success' : 'badge-warning'}`}>
                            {row.Status === 'final' ? 'Final' : 'In Progress'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-bold">
                    <td colSpan="2">Total</td>
                    <td className="text-right">{formatCents(totalSpendCents)}</td>
                    <td className="text-right">
                      {configuredAllocationGroups.length > 0
                        ? formatCents(allocationTotals.totalCommittedCents)
                        : '—'}
                    </td>
                    <td className="text-right">
                      {configuredAllocationGroups.length > 0 ? (
                        <span>
                          {formatCents(allocationTotals.totalFytdSpendCents)}
                          {allocationTotals.totalPercentDrawn != null && (
                            <span className="text-secondary-500 text-xs block font-normal">
                              {allocationTotals.totalPercentDrawn.toFixed(1)}% of prepaid
                            </span>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      <TrendBadge status={allocationTotals.trendStatus} />
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
              <div className="mt-5 p-4 bg-secondary-50 border border-secondary-200 rounded-lg">
                <div className="text-xs font-semibold uppercase tracking-wide text-secondary-500 mb-3">
                  Column guide
                </div>
                <div className="space-y-2">
                  {MONTHLY_USAGE_COLUMNS.map((col) => (
                    <p key={col.key} className="text-sm text-secondary-600 m-0">
                      <span className="font-semibold text-secondary-800">{col.label}</span>
                      {' — '}
                      {col.description}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {annualData?.groups?.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">
                  Fiscal Year to Date ({getFiscalYearLabel()})
                </h3>
                <p className="text-sm text-secondary-500 mt-1">
                  Fiscal year starts in {fiscalYearStartMonthName}
                </p>
              </div>
              <div className="card-body overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Billing Group</th>
                      <th className="text-right">YTD Spend</th>
                      <th className="text-right">% of YTD Total</th>
                      <th className="text-right">Months</th>
                    </tr>
                  </thead>
                  <tbody>
                    {annualData.groups.map((row) => (
                      <tr key={row.CursorGroupId}>
                        <td className="font-medium">{row.GroupName}</td>
                        <td className="text-right">{formatCents(row.TotalSpendCents)}</td>
                        <td className="text-right">
                          {annualTotalCents > 0
                            ? ((row.TotalSpendCents / annualTotalCents) * 100).toFixed(1)
                            : '0.0'}%
                        </td>
                        <td className="text-right">{row.MonthsIncluded}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-bold">
                      <td>Total</td>
                      <td className="text-right">{formatCents(annualTotalCents)}</td>
                      <td className="text-right">100%</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {hasCostAllocation && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">
                  Cost Allocation — Committed Prepaid ({getFiscalYearLabel()})
                </h3>
                <p className="text-sm text-secondary-500 mt-1">
                  Draw-down against annual committed prepaid by billing group
                </p>
              </div>
              <div className="card-body space-y-6">
                {configuredAllocationGroups.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="stat-card">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="stat-value">{formatCents(allocationTotals.totalCommittedCents)}</div>
                          <div className="stat-label">Total Committed</div>
                        </div>
                        <DollarSign className="w-8 h-8 text-primary-600" />
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="stat-value">{formatCents(allocationTotals.totalFytdSpendCents)}</div>
                          <div className="stat-label">Fiscal YTD Spend</div>
                        </div>
                        <Calendar className="w-8 h-8 text-warning-600" />
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="stat-value">{formatCents(allocationTotals.totalRemainingCents)}</div>
                          <div className="stat-label">Total Remaining</div>
                        </div>
                        <TrendingDown className="w-8 h-8 text-success-600" />
                      </div>
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Billing Group</th>
                        <th className="text-right">Committed Prepaid</th>
                        <th className="text-right">Fiscal YTD Spend</th>
                        <th className="text-right">Remaining</th>
                        <th className="text-right">% Drawn Down</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(costAllocation?.groups || []).map((row) => (
                        <tr key={row.cursorGroupId}>
                          <td className="font-medium">{row.groupName}</td>
                          <td className="text-right">
                            {row.isConfigured ? formatCents(row.committedPrepaidCents) : (
                              <span className="text-secondary-400">Not set</span>
                            )}
                          </td>
                          <td className="text-right">{formatCents(row.fytdSpendCents)}</td>
                          <td className="text-right">
                            {row.isConfigured ? (
                              <span className={row.remainingCents < 0 ? 'text-error-600 font-medium' : ''}>
                                {formatCents(row.remainingCents)}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="text-right">
                            {row.isConfigured && row.percentDrawn != null ? (
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-24 h-2 bg-secondary-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${row.percentDrawn > 100 ? 'bg-error-500' : 'bg-primary-500'}`}
                                    style={{ width: `${Math.min(row.percentDrawn, 100)}%` }}
                                  />
                                </div>
                                <span className="w-12 text-right">{row.percentDrawn.toFixed(1)}%</span>
                              </div>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {configuredAllocationGroups.length > 0 && (
                      <tfoot>
                        <tr className="font-bold">
                          <td>Total (configured)</td>
                          <td className="text-right">{formatCents(allocationTotals.totalCommittedCents)}</td>
                          <td className="text-right">{formatCents(allocationTotals.totalFytdSpendCents)}</td>
                          <td className="text-right">{formatCents(allocationTotals.totalRemainingCents)}</td>
                          <td className="text-right">
                            {allocationTotals.totalPercentDrawn != null
                              ? `${allocationTotals.totalPercentDrawn.toFixed(1)}%`
                              : '—'}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                {configuredAllocationGroups.length === 0 && (
                  <p className="text-sm text-secondary-500">
                    No committed prepaid values configured for this fiscal year. An admin can set them under Admin → Monthly Usage Reports.
                  </p>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MonthlyUsage;
